import { last, sortBy } from "lodash";
import moment from "moment";
import { ProfileExport } from "PFTypes";

import { CHARS_PER_POINT } from "./constants";
import { PptxgenJsTextOptions, TextItem } from "./types";

export const ptsToInch = (pts: number) => pts / 72;
export const inchToPts = (inch: number) => inch * 72;

const defaultSpace = ptsToInch(5);

const sortByDate = (
  internalExperiences: ProfileExport["internal_experiences"],
  prop: "start_date" | "end_date"
) =>
  sortBy(internalExperiences, (experience) => {
    const dateRange = experience.metadata.date_range || {};
    if (!dateRange[prop]) {
      return Infinity;
    } else if (dateRange.current) {
      return -Infinity;
    } else {
      return -moment(dateRange[prop]);
    }
  });

export const sortInternalExperiences = (
  internalExperiences: ProfileExport["internal_experiences"]
): ProfileExport["internal_experiences"] => {
  const idSorted = sortBy(internalExperiences, "id").reverse();
  const endDateSorted = sortByDate(idSorted, "end_date");
  return sortByDate(endDateSorted, "start_date");
};

const getCharsInLine = (options: PptxgenJsTextOptions) =>
  inchToPts(
    (options.w || 0) -
      ptsToInch(options.bullet?.indent || options.indent || 0) -
      (options.softBreakBefore ? ptsToInch(20) : 0)
  ) *
    CHARS_PER_POINT *
    (8 / (options.fontSize || 8)) -
  inchToPts(2 * defaultSpace);

const splitEveryN = (str: string, len: number, arr: string[] = []): string[] => {
  if (str.length === 0) {
    return arr;
  }

  arr.push(str.slice(0, len));
  return splitEveryN(str.slice(len), len, arr);
};

const getLinesCount = (text: string, options: PptxgenJsTextOptions): number => {
  const textArray = text.replace(/\r/g, "").split(/\n/);
  const charsInLine = getCharsInLine(options);
  const newLines: string[][] = textArray.map((txt) => {
    if (txt.length <= charsInLine) {
      return [txt];
    }
    const words = txt
      .split(" ")
      .map((word) => {
        if (word.length <= charsInLine) {
          return word;
        }
        return splitEveryN(word, charsInLine);
      })
      .flat();

    return words.reduce((acc, curr) => {
      const lastItem = last(acc);
      if (lastItem) {
        const newItem = `${lastItem} ${curr}`;
        if (newItem.length <= charsInLine) {
          return [...acc.slice(0, -1), newItem];
        }
        return [...acc, curr];
      }
      return [curr];
    }, []);
  });

  return newLines.map(({ length }) => length).reduce((acc, curr) => acc + curr, 0);
};

const splitParagraph = (text: string, options: PptxgenJsTextOptions) => {
  const charsInLine = getCharsInLine(options);
  const words = text.split(" ");
  const lines = words.reduce((acc: TextItem[], curr) => {
    const lastItem = last(acc);
    if (!lastItem) {
      return [{ text: curr, options }];
    }
    const newText = `${lastItem.text} ${curr}`;
    if (newText.length > charsInLine) {
      return acc.concat({ text: curr, options });
    }
    return acc.slice(0, -1).concat({ text: newText, options });
  }, []);

  return lines;
};

export const limitText = (text: string, options: PptxgenJsTextOptions): TextItem[] => {
  const textArray: TextItem[] = text
    .trim()
    .replace(/\r/g, "")
    .split(/\n/)
    .map((text) => ({ text, options: {} }));

  return limitLines(textArray, options);
};

export const limitLines = (textArray: TextItem[], options: PptxgenJsTextOptions): TextItem[] => {
  const maxHeight = inchToPts(options.h || 0);
  const heightPerText = textArray.map(
    (row) =>
      getLinesCount(row.text, { ...options, ...row.options }) *
      Math.max(row.options.lineSpacing || 10, row.options.fontSize || 0)
  );

  const lastTextIndex = heightPerText.findIndex((_, index, arr) => {
    const subArray = arr.slice(0, index + 1);
    const sum = subArray.reduce((acc, curr) => acc + curr, 0);
    return sum > maxHeight;
  });

  if (lastTextIndex === 0) {
    return limitLines(splitParagraph(textArray[0].text, options), options);
  }

  return lastTextIndex === -1 ? textArray : textArray.slice(0, lastTextIndex);
};

export const splitLinesByContent = (
  textArray: TextItem[],
  ...options: PptxgenJsTextOptions[]
): TextItem[][] => {
  if (!options?.length) {
    return [textArray];
  }

  let linesLeft = [...textArray];
  let optionsLeft = [...options];
  const linesByContent: TextItem[][] = [];

  while (linesLeft.length > 0) {
    const currentOptions = optionsLeft[0] || last(options);
    const lines = limitLines(linesLeft, currentOptions);

    linesByContent.push(lines);
    optionsLeft = optionsLeft.slice(1);
    linesLeft = linesLeft.slice(lines.length);
  }

  return linesByContent;
};

export const getAdjustedHeight = (text: string, options: PptxgenJsTextOptions) => {
  const textArray = limitText(text, options);
  const linesPerRow = textArray.map((item) => getLinesCount(item.text, { ...options, ...item.options }));
  const linesCount = linesPerRow.reduce((acc, curr) => acc + curr, 0);

  return Math.min(ptsToInch(linesCount * (options.lineSpacing || 10)), options.h || 0) + 2 * defaultSpace;
};
